<template>
	<div>
		<div class="modal-small-toolbar">
			<div>
				<span v-if="! editingExpense && ! creatingExpense">Expenses</span>
				<div v-if="creatingExpense || editingExpense" class="space-x-2">
					<a @click="creatingExpense = editingExpense = false">Expenses</a>
					<span>/</span>
			        <span v-if="creatingExpense">Create New Expense</span>
			        <span v-if="editingExpense">Edit Expense</span>
				</div>
			</div>
		</div>
		<div v-if="!editingExpense && !creatingExpense" class="w-full p-4 mr-2">
        	<table v-if="leases.selectedLease?.expenses?.length" class="w-full">
        		<thead>
        			<tr>
	                    <th class="pl-4">Type</th>
	                    <th class="text-center">Start</th>
	        			<th class="text-center">End</th>
	        			<th class="text-right pr-8">Base Amount</th>
	        			<th>Frequency</th>
	        			<th class="text-right">PSF</th>
	        			<th class="text-center">Liability?</th>
	        			<th></th>
	        		</tr>
        		</thead>
        		<tbody>
        			<tr v-for="expense in leases.selectedLease.expenses" :key="expense.id">
                        <td class="pl-4">{{ expense.type.name }}</td>
                        <td class="text-center">{{ expense.start_date | date }}</td>
        				<td class="text-center">{{ expense.end_date | date }}</td>
        				<td class="text-right pr-8">{{ formatCurrency(expense.amount, expense.frequency == 'PSF' ? 2 : 0) }}</td>
        				<td>{{ expense.frequency }}</td>
        				<td class="text-right">{{ formatCurrency(expense.amount_psf, 2) }}</td>
                        <td>
                            <div class="flex justify-center">
                                <div v-if="expense.is_liability == null">-</div>
                                <div v-else>{{ expense.is_liability ? 'Yes' : 'No' }}</div>
                            </div>
                        </td>
        				<td class="flex justify-center space-x-3">
				            <button @click="editExpense(expense.id)" type="button">
                                <inline-svg name="edit-pencil" classes="w-5 h-5 cursor-pointer"></inline-svg>
                            </button>
				            <button @click="confirmDeleteExpense(expense.id)" type="button">
                                <inline-svg name="trash"></inline-svg>
                            </button>
        				</td>
					</tr>
				</tbody>
			</table>
            <div v-else class="mt-8 flex flex-col h-full items-center justify-center space-y-2">
                <span class="text-xl">No Expenses Found</span>
                <button
                    v-if="canCreateExpense"
                    type="button"
                    @click.prevent="createExpense"
                    class="flex items-center btn btn-small btn-indigo">
                        <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">New Expense</span>
                </button>
                <div v-else class="text-sm text-gray-500">
                    You don't have permission to create expenses
                </div>
            </div>
			<button v-if="leases.selectedLease?.expenses?.length && canCreateExpense" @click="createExpense" type="button" class="my-3 flex items-center float-right btn btn-small btn-indigo">
				<inline-svg name="add-white"></inline-svg>
				<span>Create Expense</span>
			</button>
		</div>

		<div v-else>
            <div class="mt-3 ml-3 border-b border-gray-200">
            	<div class="flex items-center">
	                <nav class="-mb-px flex space-x-2" aria-label="Steps">
	                    <button
	                        v-for="(step, slug) in enabledSteps" :key="slug"
	                        @click="currentStep = slug"
	                        class="py-2 px-3 border border-gray-200 flex whitespace-nowrap rounded-t-lg text-sm"
	                        :class="{ 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != currentStep, 'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white': slug == currentStep }">
	                            {{ step.label }}
	                        <span
	                            v-if="step.showCount"
	                            class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
	                            :class="{ 'bg-gray-200 text-gray-900': slug != currentStep, 'bg-blue-800 text-white': slug == currentStep }">
	                                {{ step.count }}
	                        </span>
	                    </button>
	                </nav>
	                <div class="ml-5" v-if="currentStep == 'escalations'">
	                	<button @click="escalateDown" type="button" class="text-gray-800 hover:text-gray-700 underline">Escalate Down</button>
	                </div>
	                <div v-if="currentStep == 'free_months'" class="flex items-center">
		                <div class="ml-6 text-sm">
		                	<span class="font-semibold" v-html="freeMonthsAvailable"></span> Free {{ freeMonthsAvailable | pluralize('Month') }} Available
		                </div>
	                    <div class="ml-6">
	                        <button @click="$eventHub.$emit('change-section', 'lease-info')" type="button" class="mb-1.5 text-sm underline hover:text-gray-700">
	                    		Manage Free Months
	                    	</button>
	                    </div>
                    </div>
	            </div>
            </div>

			<div v-if="currentStep == 'expense_info'" class="ml-3 border-l border-gray-200">
	            <form>
	                <div class="p-2 mr-2">
	                    <div class="flex">
		                    <div class="flex flex-1 flex-col shadow-md rounded mr-6 p-6 bg-gray-100 border border-gray-200">
		                        <div class="mb-4 flex gap-4">
		                            <div>
		                                <label for="start_date">Start Date</label>
		                                <date-picker
		                                    v-model="expense.start_date"
		                                    lang="en"
		                                    :first-day-of-week="7"
		                                    format="MM/DD/YYYY"
		                                    :value-type="valueType"
		                                    input-name="start_date"
		                                    input-class="form-input"
		                                    :input-attr="{ size: 11, required: true }">
		                                </date-picker>
		                                <div v-if="errors.start_date" class="form-error">{{ errors.start_date[0] }}</div>
		                            </div>
		                            <div>
		                                <label for="end_date">End Date</label>
		                                <date-picker
		                                    v-model="expense.end_date"
		                                    lang="en"
		                                    :first-day-of-week="7"
		                                    format="MM/DD/YYYY"
		                                    :value-type="valueType"
		                                    input-name="end_date"
		                                    input-class="form-input"
		                                    :input-attr="{ size: 11, required: true }">
		                                </date-picker>
		                                <div v-if="errors.end_date" class="form-error">{{ errors.end_date[0] }}</div>
		                            </div>
		                            <div>
		                                <label for="type">Type</label>
		                                <select v-model="expense.type_id" @change="setExpenseType($event)" class="mt-1 form-select min-w-fit">
		                                	<option value=""></option>
		                                	<option v-for="type in expenseTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
		                                </select>
		                                <div v-if="errors.type_id" class="form-error">{{ errors.type_id[0] }}</div>
		                            </div>
			                        <div>
										<label for="frequency">Frequency</label>
			                        	<select v-model="expense.frequency" class="mt-1 form-select">
			                        		<option value=""></option>
			                        		<option value="PSF">PSF</option>
			                        		<option value="Monthly">Monthly</option>
			                        		<option value="Quarterly">Quarterly</option>
			                        		<option value="Annual">Annual</option>
			                        		<option value="Semi-Annual">Semi-Annual</option>
			                        		<option value="One Time">One Time</option>
			                        	</select>
			                        	<div v-if="errors.frequency" class="form-error">{{ errors.frequency[0] }}</div>
			                        </div>
			                        <div>
										<label for="expense_amount">Amount</label>
										<currency-input v-model="expense.amount_in_dollars" :size="11" name='expense_amount' is-decimal></currency-input>
										<div v-if="errors.amount" class="form-error">{{ errors.amount[0] }}</div>
			                        </div>
								</div>
		                        <div>
		                            <textarea v-model="expense.note" class="form-textarea text-sm w-full" cols="7" rows="5" name="note" placeholder="Expense Notes"></textarea>
		                        </div>
		                    </div>

		                    <div>
		                        <div class="flex justify-between items-center border-b border-dotted border-gray-300 mb-4 pb-3">
		                            <label class="mr-10">Liability</label>
		                            <toggle-switch v-model="expense.is_liability" name="is_liability"></toggle-switch>
		                        </div>
		                        <div class="hidden flex justify-between items-center">
		                            <label class="mr-10">Pass Thru</label>
		                            <toggle-switch v-model="expense.is_pass_thru" name="is_pass_thru"></toggle-switch>
		                        </div>
		                    </div>
						</div>
	                </div>
	            </form>
	        </div>

	        <div v-if="currentStep == 'escalations'" class="ml-3 border-l border-gray-200">
	        	<div class="max-h-[500px] overflow-y-auto">
		        	<table id="escalations" class="relative w-full">
		        		<thead>
		        			<tr>
		        				<th class="sticky top-0 pl-4 text-center">Year</th>
		        				<th class="sticky top-0 text-center" v-for="month in expenseMonths" :key="month">{{ month }}</th>
		        			</tr>
		        		</thead>
		        		<tbody>
		        			<tr v-for="(row, year) in expense.timeline" :key="year">
		        				<td class="pl-4 text-center">{{ year }}</td>
		        				<td class="px-0.5 text-center" v-for="(month, month_number) in row.months" :key="month_number">
		        					<input
		        						@input="setEscalation($event)"
		        						@blur="setPreviousEscalationInput($event)"
		        						:data-year="year"
		        						:data-month="month_number"
		        						type="text"
		        						class="form-input text-right"
		        						size="5" />
		        				</td>
		        			</tr>
		        		</tbody>
		        	</table>
		        </div>
	        </div>

	        <div v-if="currentStep == 'free_months'" class="ml-3 border-l border-gray-200">
	        	<div class="max-h-[500px] overflow-y-auto">
		        	<table id="free_months" class="relative w-full">
		        		<thead>
		        			<tr>
		        				<th class="sticky top-0 pl-4 text-center">Year</th>
		        				<th class="sticky top-0 text-center" v-for="month in expenseMonths" :key="month">{{ month }}</th>
		        			</tr>
		        		</thead>
		        		<tbody>
		        			<tr v-for="(row, year) in expense.timeline" :key="year">
		        				<td class="pl-4 text-center">{{ year }}</td>
		        				<td class="px-0.5 text-center" v-for="(month, month_number) in row.months" :key="month_number">
		        					<input
		        						@click="setFreeMonth($event)"
		        						:data-year="year"
		        						:data-month="month_number"
		        						type="checkbox"
		        						class="form-checkbox w-6 h-6" />
		        				</td>
		        			</tr>
		        		</tbody>
		        	</table>
		        </div>
	        </div>
		</div>
	</div>
</template>

<script>
	import { mapState } from 'vuex'
	import moment from 'moment';
	import DatePicker from 'vue2-datepicker';

	export default {
        components: {
            DatePicker
        },

        props: {
            formErrors: {
                type: [String, Object],
                required: false
            }
        },

		data() {
			return {
				editingExpense: false,
				creatingExpense: false,
				currentStep: 'expense_info',
				selectedExpenseId: '',
				expenseTypes: '',
				previousEscalationInput: null,
				freeMonthsAvailable: 0,

                valueType: {
                    value2date: (value) => {
                        return value ? moment(value, 'YYYY-MM-DD').toDate() : null
                    },
                    date2value: (date) => {
                        return date ? moment(date).format('YYYY-MM-DD') : null
                    }
                }
			}
		},

		created() {
			this.$parent.$parent.$on('expenseSaved', (expenseId) => {
				this.creatingExpense = false;
				this.editingExpense = true;

				this.selectedExpenseId = expenseId;
			});
		},

		mounted() {
			this.$eventHub.$on('leaseSaved', () => {
				this.calculateFreeMonthsAvailable();
			});

            axios.get('/api/expense-types').then((response) => {
				this.expenseTypes = response.data;
			}).catch((error) => {
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Error pulling Expense Types, please try again later'
                });
           	});
		},

		watch: {
			selectedExpenseId() {
				this.$emit('selectedExpenseChange', this.selectedExpenseId);
			},

			expenses() {
				if (this.leases.selectedLease) {
					if (this.creatingExpense && ! _.find(this.leases.selectedLease.expenses, ['id', 'new'])) {
						this.selectedExpenseId = _.last(this.leases.selectedLease.expenses).id;
						this.creatingExpense = false;
						this.editingExpense = true;
					}
				}
			},

			currentStep() {
				if (this.currentStep == 'escalations') {
					this.setEscalations();
				}

				if (this.currentStep == 'free_months') {
					this.setFreeMonths();
					this.calculateFreeMonthsAvailable();
				}
			}
		},

		computed: {
		    enabledSteps() {
		        // v-for used to be paired with v-if="step.enabled"; Vue 2 runs
		        // v-for first so the list was already filtered. Same result, and
		        // it does not break when the directive priority reverses in Vue 3.
		        const source = this.steps;
		        return Array.isArray(source)
		            ? source.filter(entry => entry.enabled)
		            : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
		    },

			...mapState(['sites']),
		...mapState(['leases']),
        canCreateExpense() {
            return this.$gate.can('create', 'site');
        },

			steps() {
				return {
					expense_info: {
						label: "Expense",
						enabled: true
					},
					escalations: {
						label: "Escalations",
						enabled: this.expense.id != 'new',
						showCount: true,
						count: (this.expense.escalations || []).length
					},
					free_months: {
						label: "Free Months",
						enabled: this.expense.id != 'new',
						showCount: true,
						count: (this.expense.free_months || []).length
					},
				};
			},

			expenseMonths() {
				let months = [];

				for (var i = 0; i < 12; i++) {
					months.push(moment(this.expense.start_date).add(i, 'months').format('MMM'));
				}

				return months;
			},

            expense() {
            	return this.selectedExpenseId == ''
            		? ''
            		: _.find(this.leases.selectedLease.expenses, ['id', this.selectedExpenseId]);
            },

            expenses() {
            	return this.leases.selectedLease ? this.leases.selectedLease.expenses : {};
            },

            errors() {
                return this.formErrors;
            }
		},

		methods: {
			setEscalation(event) {
				let year = parseInt(event.target.dataset.year);
				let month = parseInt(event.target.dataset.month);

				let escalationRecord = _.find(this.expense.escalations, (row) => {
					return row.year_number == year && row.month_number == month
				});

				if (escalationRecord) {
					escalationRecord.amount = event.target.value;
				} else {
					this.expense.escalations.push({
						expense_id: this.expense.id,
						year_number: year,
						month_number: month,
						amount: event.target.value
					});
				}
			},

			setEscalations() {
				this.expense.escalations.forEach(escalation => {
					this.$nextTick(() => {
						let input = document.querySelector(`#escalations input[data-year="${escalation.year_number}"][data-month="${escalation.month_number}"]`);

						input.value = escalation.amount;
					});
				})
			},

			setPreviousEscalationInput(event) {
				this.previousEscalationInput = event.target;
			},

			escalateDown() {
				let year = parseInt(this.previousEscalationInput.dataset.year);
				let month = this.previousEscalationInput.dataset.month;
				let inputs = document.querySelectorAll(`#escalations [data-month="${month}"]`);

				for (var i = 0; i < inputs.length; ++i) {
					if (parseInt(inputs[i].dataset.year) > year) {
						inputs[i].value = this.previousEscalationInput.value;

						inputs[i].dispatchEvent(new Event('input', { bubbles: true }));
					}
 				}
			},

			setFreeMonth(event) {
				let year = parseInt(event.target.dataset.year);
				let month = parseInt(event.target.dataset.month);

				if (event.target.checked) {
					this.expense.free_months.push({
						expense_id: this.expense.id,
						year_number: year,
						month_number: month
					});
				} else {
					_.remove(this.expense.free_months, (row) => {
						return row.year_number == year && row.month_number == month
					});
				}

				this.calculateFreeMonthsAvailable();
			},

			setFreeMonths() {
				if (! this.expense.free_months.length) {
					return;
				}

				this.expense.free_months.forEach(freeMonth => {
					this.$nextTick(() => {
						let input = document.querySelector(`#free_months input[data-year="${freeMonth.year_number}"][data-month="${freeMonth.month_number}"]`);

						input.checked = 'checked';
					});
				})
			},

            calculateFreeMonthsAvailable() {
            	this.$nextTick(() => {
            		let freeMonths = parseInt(this.leases.selectedLease.free_months);

	            	if (isNaN(freeMonths)) {
	            		this.freeMonthsAvailable = 0;
	            	} else {
	            		this.freeMonthsAvailable = parseInt(freeMonths) - document.querySelectorAll(`#free_months input:checked`).length;
	            	}

	            	if (this.freeMonthsAvailable == 0) {
	            		this.disableFreeMonths();
	            	} else {
	            		this.enableFreeMonths();
	            	}
            	});
            },

            enableFreeMonths() {
            	document.querySelectorAll(`#free_months input`).forEach(input => {
           			input.disabled = false;
            	});
            },

            disableFreeMonths() {
            	document.querySelectorAll(`#free_months input`).forEach(input => {
            		if (! input.checked) {
            			input.disabled = 'disabled';
            		}
            	});
            },

			createExpense() {
				this.editingExpense = false;
				this.creatingExpense = true;

				if (typeof this.leases.selectedLease.expenses == 'undefined') {
					this.leases.selectedLease.expenses = [];
				}

				this.leases.selectedLease.expenses.push({
					id: 'new',
					expensable_id: this.leases.selectedLease.id,
					expensable_type: 'App\\Lease',
					type_id: '',
					type: { name: '' },
					frequency: '',
					amount_in_dollars: '$0',
					start_date: moment(this.leases.selectedLease.start_date).format('YYYY-MM-DD'),
					end_date: moment(this.leases.selectedLease.end_date).format('YYYY-MM-DD'),
					is_liability: 0,
					is_pass_thru: 0,
					escalations: [],
					free_months: [],
					note: ''
				});

				this.selectedExpenseId = 'new';
			},

			setExpenseType(event) {
				this.expense.type.name = _.find(this.expenseTypes, ['id', parseInt(event.target.value)]).name;
			},

			editExpense(expenseId) {
				this.selectedExpenseId = expenseId;
                
                // Ensure the expense has an escalations array
                const expense = _.find(this.leases.selectedLease.expenses, ['id', expenseId]);
                if (!expense?.escalations) {
                    expense.escalations = [];
                }

				this.creatingExpense = false;
				this.editingExpense = true;
			},

            removeExpense(expenseId) {
                _.remove(this.leases.all.expenses, (expense) => { return expense.id == expenseId });
                _.remove(this.leases.selectedLease.expenses, (expense) => { return expense.id == expenseId });

                this.$emit('filterRequest');

                this.$forceUpdate();
            },

            confirmDeleteExpense(expenseId) {
                if (expenseId == "new") {
                    this.removeExpense('new');

                    return;
                }

                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this Expense?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteExpense(expenseId);
                    }
                });
            },

            deleteExpense(expenseId) {
                axios.delete('/expenses/' + expenseId)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });

                        this.removeExpense(expenseId);

                        this.$eventHub.$emit('reloadSite');
                    })
                    .catch((error) => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },
		},
	}
</script>
